import { COLORS, FIELD, goalMouth, type PitchSkin } from "@golazo/shared";
import type { Graphics } from "pixi.js";

/** Dimensiones reglamentarias FIFA (40 m x 20 m). Solo render. */
const LENGTH_M = 40;
const CENTER_CIRCLE_M = 3;
const PENALTY_ARC_M = 6;
const SECOND_PENALTY_M = 10;
const CORNER_ARC_M = 0.25;
const SUB_ZONE_M = 3;
const WIDTH_M = 20;

function scaleX(): number {
  return FIELD.WIDTH / LENGTH_M;
}

/**
 * El mundo (1600x1000) no tiene la proporcion de una pista FIFA (40x20 m), asi
 * que cada eje lleva su propia escala: los metros verticales miden mas que los
 * horizontales y las figuras "circulares" se dibujan como elipses. Antes todo
 * usaba scaleX y el area salia un 20% corta en vertical.
 */
function scaleY(): number {
  return FIELD.HEIGHT / WIDTH_M;
}

function strokeFor(skin: PitchSkin) {
  const color = skin.line || COLORS.LINE;
  return { width: FIELD.LINE_WIDTH, color, alpha: 0.92 } as const;
}

interface LineStroke {
  readonly width: number;
  readonly color: number;
  readonly alpha: number;
}

/** Elipse por muestreo: Pixi solo sabe de arcos circulares. */
function ellipseArc(
  g: Graphics,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  from: number,
  to: number,
  stroke: LineStroke,
): void {
  const steps = Math.max(10, Math.ceil((Math.abs(to - from) / (Math.PI / 2)) * 14));
  for (let i = 0; i <= steps; i += 1) {
    const a = from + ((to - from) * i) / steps;
    const x = cx + Math.cos(a) * rx;
    const y = cy + Math.sin(a) * ry;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.stroke(stroke);
}

/** Arco de esquina reglamentario (25 cm), con un minimo visible en pantalla. */
function drawCornerArc(
  g: Graphics,
  cx: number,
  cy: number,
  startAngle: number,
  stroke: ReturnType<typeof strokeFor>,
): void {
  const rx = Math.max(CORNER_ARC_M * scaleX(), FIELD.LINE_WIDTH * 3);
  const ry = Math.max(CORNER_ARC_M * scaleY(), FIELD.LINE_WIDTH * 3);
  ellipseArc(g, cx, cy, rx, ry, startAngle, startAngle + Math.PI / 2, stroke);
}

/**
 * Area de penalti: dos cuartos de circulo de 6 m centrados en cada poste, unidos
 * por un tramo recto paralelo a la linea de gol y del ancho de la porteria.
 */
function drawPenaltyArea(
  g: Graphics,
  side: "left" | "right",
  mouth: ReturnType<typeof goalMouth>,
  stroke: ReturnType<typeof strokeFor>,
): void {
  const rx = PENALTY_ARC_M * scaleX();
  const ry = PENALTY_ARC_M * scaleY();
  const { top, bottom } = mouth;
  const gx = side === "left" ? 0 : FIELD.WIDTH;
  const dir = side === "left" ? 1 : -1;

  // Cuarto superior: de la linea de gol hacia el centro del campo.
  ellipseArc(g, gx, top, rx, ry, -Math.PI / 2, side === "left" ? 0 : -Math.PI, stroke);
  // Cuarto inferior, simetrico.
  ellipseArc(g, gx, bottom, rx, ry, side === "left" ? 0 : Math.PI, Math.PI / 2, stroke);
  // Cierre recto entre los dos extremos.
  g.moveTo(gx + dir * rx, top).lineTo(gx + dir * rx, bottom).stroke(stroke);
}

/** Punto de penalti (6 m) y segundo penalti (10 m). */
function drawPenaltyMarks(
  g: Graphics,
  side: "left" | "right",
  stroke: ReturnType<typeof strokeFor>,
): void {
  const cy = FIELD.HEIGHT / 2;
  const dot = Math.max(FIELD.LINE_WIDTH * 1.2, 4);
  for (const [meters, size] of [
    [PENALTY_ARC_M, dot],
    [SECOND_PENALTY_M, dot * 1.3],
  ] as const) {
    const d = meters * scaleX();
    const x = side === "left" ? d : FIELD.WIDTH - d;
    g.circle(x, cy, size).fill({ color: stroke.color, alpha: stroke.alpha });
  }
}

/** Zona de sustitucion (3 m a cada lado del centro en la banda). */
function drawSubstitutionZones(g: Graphics, stroke: ReturnType<typeof strokeFor>): void {
  const mid = FIELD.WIDTH / 2;
  const zone = SUB_ZONE_M * scaleX();
  const mark = 14;
  const yTop = 0;
  const yBot = FIELD.HEIGHT;

  for (const x of [mid - zone, mid + zone]) {
    g.moveTo(x, yTop).lineTo(x, yTop + mark).stroke({ ...stroke, alpha: stroke.alpha * 0.65 });
    g.moveTo(x, yBot).lineTo(x, yBot - mark).stroke({ ...stroke, alpha: stroke.alpha * 0.65 });
  }
}

/** Lineas de futsal segun FIFA (adaptadas al tamano del mundo). */
export function drawFutsalLines(g: Graphics, skin: PitchSkin): void {
  const { WIDTH, HEIGHT } = FIELD;
  const stroke = strokeFor(skin);
  const lineColor = stroke.color;
  const mouth = goalMouth();
  const cx = WIDTH / 2;
  const cy = HEIGHT / 2;
  const centerR = CENTER_CIRCLE_M * scaleX();

  // Perimetro.
  g.rect(0, 0, WIDTH, HEIGHT).stroke(stroke);

  // Linea de medio campo.
  g.moveTo(cx, 0).lineTo(cx, HEIGHT).stroke(stroke);

  // Circulo central (3 m): elipse, porque el metro no mide igual en cada eje.
  ellipseArc(g, cx, cy, centerR, CENTER_CIRCLE_M * scaleY(), 0, Math.PI * 2, stroke);
  g.circle(cx, cy, FIELD.LINE_WIDTH * 1.8).fill(lineColor);

  // Areas de penalti (arcos de 6 m).
  drawPenaltyArea(g, "left", mouth, stroke);
  drawPenaltyArea(g, "right", mouth, stroke);

  // Puntos de penalti (6 m) y segundo penalti (10 m).
  drawPenaltyMarks(g, "left", stroke);
  drawPenaltyMarks(g, "right", stroke);

  // Arcos de esquina (25 cm).
  drawCornerArc(g, 0, 0, 0, stroke);
  drawCornerArc(g, WIDTH, 0, Math.PI / 2, stroke);
  drawCornerArc(g, WIDTH, HEIGHT, Math.PI, stroke);
  drawCornerArc(g, 0, HEIGHT, (3 * Math.PI) / 2, stroke);

  // Zonas de sustitucion en bandas.
  drawSubstitutionZones(g, stroke);
}

/** Minimapa simplificado de futsal. */
export function drawFutsalRadarMarks(
  g: Graphics,
  ix: number,
  iy: number,
  iw: number,
  ih: number,
  lineColor: number,
): void {
  const cx = ix + iw / 2;
  const cy = iy + ih / 2;
  const sx = iw / LENGTH_M;
  const sy = ih / WIDTH_M;
  const prx = PENALTY_ARC_M * sx;
  const pry = PENALTY_ARC_M * sy;
  const postSpan = ih * 0.11;
  const yTop = cy - postSpan;
  const yBot = cy + postSpan;
  const stroke = { width: 1, color: lineColor, alpha: 0.34 } as const;

  g.moveTo(cx, iy).lineTo(cx, iy + ih).stroke(stroke);
  ellipseArc(g, cx, cy, CENTER_CIRCLE_M * sx, CENTER_CIRCLE_M * sy, 0, Math.PI * 2, {
    ...stroke,
    alpha: 0.3,
  });

  ellipseArc(g, ix, yTop, prx, pry, -Math.PI / 2, 0, stroke);
  ellipseArc(g, ix, yBot, prx, pry, 0, Math.PI / 2, stroke);
  g.moveTo(ix + prx, yTop).lineTo(ix + prx, yBot).stroke(stroke);

  const rx = ix + iw;
  ellipseArc(g, rx, yTop, prx, pry, -Math.PI / 2, -Math.PI, stroke);
  ellipseArc(g, rx, yBot, prx, pry, Math.PI, Math.PI / 2, stroke);
  g.moveTo(rx - prx, yTop).lineTo(rx - prx, yBot).stroke(stroke);
}

export function isFutsalSkin(skin: PitchSkin): boolean {
  return skin.id === "terrazzo";
}
